/** ICU substitution values for a curated error template; the backend sends string-valued params. */
export type MessageArgs = Record<string, string>;

/**
 * The localizable error shape the backend sends: on every Smithy exception (via the
 * LocalizableErrorMixin) and on each `UpdateEventResponse.syncErrorDetails` entry. Read structurally
 * rather than through the generated client type so a partial-failure detail or a caught exception
 * resolves whatever the running backend sends, without waiting on a client regeneration in the
 * version set.
 */
export interface LocalizableErrorLike {
  message?: string;
  messageKey?: string;
  messageParams?: MessageArgs;
}

/**
 * Param names the backend fills with a stable token (mirrors the FieldToken/EntityToken values in
 * the Lambda's benevityErrorMap) that only reads correctly once mapped to a localized label/noun.
 * Until that mapping ships, a key carrying one of these degrades to the generic message rather than
 * showing the raw token.
 */
const TOKEN_PARAM_NAMES = ['fieldName', 'entity', 'scope'] as const;

const GENERIC_ERROR_KEY = 'error_generic_errorOccurred';

/** Last-resort text if even the generic key has no pack loaded; mirrors the en bundle value. */
export const GENERIC_ERROR_FALLBACK = 'An error occurred';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Reads the localizable fields off an unknown error/detail, keeping only string-typed values so a
 * malformed payload can never inject a non-string into a template.
 */
export function toLocalizableError(value: unknown): LocalizableErrorLike | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const result: LocalizableErrorLike = {};
  if (typeof value.message === 'string') {
    result.message = value.message;
  }
  if (typeof value.messageKey === 'string') {
    result.messageKey = value.messageKey;
  }
  if (isRecord(value.messageParams)) {
    const params: MessageArgs = {};
    for (const [key, paramValue] of Object.entries(value.messageParams)) {
      if (typeof paramValue === 'string') {
        params[key] = paramValue;
      }
    }
    if (Object.keys(params).length > 0) {
      result.messageParams = params;
    }
  }
  return result;
}

/** Backend keys are dotted (`error.field.required`); the bundle ids are underscored. */
export function normalizeMessageKey(key: string): string {
  return key.replace(/\./g, '_');
}

function hasUnlocalizedToken(params: MessageArgs | undefined): boolean {
  return params !== undefined && TOKEN_PARAM_NAMES.some((name) => name in params);
}

/**
 * Turns an error into localized display text via the `website.errors` bundle.
 *
 * Resolves `messageKey` to a curated, localized sentence and never surfaces raw upstream or runtime
 * text. Falls back to the generic localized message when there is no key, the key is missing from
 * the pack, or the key needs a field/entity token this build does not yet localize.
 */
export function resolveLocalizedError(
  error: unknown,
  format: (id: string, args?: MessageArgs) => string,
  fallbackId: string = GENERIC_ERROR_KEY
): string {
  const localizable = toLocalizableError(error);
  if (localizable?.messageKey && !hasUnlocalizedToken(localizable.messageParams)) {
    // Always format, even with no params, so a key whose template still has unfilled placeholders
    // degrades to the fallback message rather than rendering a literal `{placeholder}`.
    const text = format(
      normalizeMessageKey(localizable.messageKey),
      localizable.messageParams ?? {}
    );
    if (text) {
      return text;
    }
  }
  // `fallbackId` lets a caller pick a surface-specific generic so an error carrying no messageKey
  // still reads better than the bare generic; last-resort is the compiled-in en text.
  return format(fallbackId) || GENERIC_ERROR_FALLBACK;
}
